import { QUALITIES, type QualitySlug } from './chordQualities';
import { parseRoot, type RootSlug } from './notes';

export interface ParsedChordName {
  /** The trimmed input. */
  label: string;
  root: RootSlug | null;
  quality: QualitySlug | null;
  /** Slash-chord bass note as written (kept, ignored for lookup, AC-49). */
  bass: string | null;
}

/**
 * Suffix to quality, lower-cased. `M` and `M7` are case-sensitive (major vs minor) and handled
 * separately, so they are left out of this map.
 */
const SUFFIX_MAP = new Map<string, QualitySlug>();
for (const q of QUALITIES) {
  SUFFIX_MAP.set(q.suffix.toLowerCase(), q.slug);
  for (const alias of q.aliases) {
    if (alias === 'M' || alias === 'M7') continue;
    SUFFIX_MAP.set(alias.toLowerCase(), q.slug);
  }
}

const BASS_RE = /^[A-Ga-g](#|b)?$/;
const ROOT_PREFIX_RE = /^([A-Ga-g])(#|b)?/;

/** Resolves a suffix such as `m7`, `maj`, `Δ7` or `M` to a quality, or null. */
export function parseQualitySuffix(rawSuffix: string): QualitySlug | null {
  const suffix = rawSuffix.replace(/\s+/g, '');
  if (suffix === 'M') return 'maj';
  if (suffix === 'M7') return 'maj7';
  return SUFFIX_MAP.get(suffix.toLowerCase()) ?? null;
}

/** Parses a chord name from a GP file or a search box. Anything it does not recognise gives `quality: null`. */
export function parseChordName(raw: string): ParsedChordName {
  const label = raw.trim();
  let text = label.replace(/♯/g, '#').replace(/♭/g, 'b');
  let bass: string | null = null;

  const slash = text.lastIndexOf('/');
  if (slash > 0) {
    const right = text.slice(slash + 1).trim();
    if (BASS_RE.test(right)) {
      bass = right;
      text = text.slice(0, slash).trim();
    }
  }

  const m = ROOT_PREFIX_RE.exec(text);
  if (!m) return { label, root: null, quality: null, bass };
  const root = parseRoot(m[0]);
  const quality = parseQualitySuffix(text.slice(m[0].length));
  return { label, root, quality: root ? quality : null, bass };
}
